import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Bracket,
  BracketSeries,
  TournamentTeamSlot,
  Prediction,
  postBountySimulatorPrediction,
} from "@portal-frontend-ssr/blast-api";
import { isMatchCompleted } from "~/shared/helpers/tournaments/isMatchCompleted";
import { mapStagesToShareablePrediction } from "../helpers/mapStagesToShareablePrediction";

type BountyCalculatorProviderValue = {
  stages: BountyStage[];
  currentStandings: BountyTeam[];
  initialTeams: BountyTeam[];

  setMatchTeamA: (matchId: string, teamId: string | null) => void;
  setMatchTeamB: (matchId: string, teamId: string | null) => void;
  setMatchWinner: (matchId: string, winnerTeamId?: string, loserTeamId?: string) => void;
  setWagerAmount: (matchId: string, amount?: number) => void;

  autofillStage: (stageId: BracketStageId) => void;
  resetStage: (stageId: BracketStageId) => void;
  randomiseWinners: (stageId: BracketStageId) => void;

  sharePrediction: () => Promise<string | null>;
  loadingPrediction: boolean;
};

const BountyCalculatorContext = createContext<BountyCalculatorProviderValue | null>(null);

export type BracketStageId = "ROUND_OF_32" | "ROUND_OF_16" | "QUARTER_FINAL" | "SEMI_FINAL" | "FINAL";

export interface BountyTeam {
  id: string;
  name: string;
  shortName: string;
  bountyValue: number;
  bountySeed: number | undefined;
  banked: number;
  eliminated: boolean;
}

export interface BountyMatch {
  id: string;
  teamA: BountyTeam | null;
  teamB: BountyTeam | null;
  winnerTeamId?: string;
  loserTeamId?: string;
  wagerAmount?: number;
}

export interface BountyStage {
  stageId: BracketStageId;
  displayName: string;
  matches: BountyMatch[];
}

function convertBracketMatchToBountyMatch(match: BracketSeries, teams: BountyTeam[]): BountyMatch {
  const teamAObj = match.teamA ? teams.find((t) => t.id === match.teamA?.uuid) || null : null;
  const teamBObj = match.teamB ? teams.find((t) => t.id === match.teamB?.uuid) || null : null;

  const completed = isMatchCompleted(match);
  const winnerTeamId = completed
    ? match.teamAScore > match.teamBScore
      ? match.teamA?.uuid
      : match.teamB?.uuid
    : undefined;
  const loserTeamId = winnerTeamId === match.teamA?.uuid ? match.teamB?.uuid : match.teamA?.uuid;

  return {
    id: match.uuid,
    teamA: teamAObj,
    teamB: teamBObj,
    winnerTeamId,
    loserTeamId,
    wagerAmount: match.metadata?._t === "bounty_match" ? (match.metadata?.wager ?? undefined) : undefined,
  };
}

function mapBracketsToStages(brackets: Bracket[], teams: BountyTeam[]): BountyStage[] {
  const playInBracket = brackets.find((b) => b.format === "single-elim-32-bounty");
  const finalBracket = brackets.find((b) => b.format === "single-elim-8-bounty");

  if (!playInBracket || !finalBracket || playInBracket?.matches?.length !== 24 || finalBracket?.matches?.length !== 7) {
    throw new Error("Brackets incomplete or invalid for bounty format");
  }

  // Round of 32
  const roundOf32 = playInBracket.matches.filter((match) => match.index >= 0 && match.index < 16);
  const roundOf16 = playInBracket.matches.filter((match) => match.index >= 16 && match.index < 24);

  // Quarter, Semi, Final
  const quarter = finalBracket.matches.filter((match) => match.index < 4);
  const semi = finalBracket.matches.filter((match) => match.index >= 4 && match.index <= 5);
  const finale = finalBracket.matches.filter((match) => match.index === 6);

  return [
    {
      stageId: "ROUND_OF_32",
      displayName: "Round of 32",
      matches: roundOf32.map((match) => convertBracketMatchToBountyMatch(match, teams)),
    },
    {
      stageId: "ROUND_OF_16",
      displayName: "Round of 16",
      matches: roundOf16.map((match) => convertBracketMatchToBountyMatch(match, teams)),
    },
    {
      stageId: "QUARTER_FINAL",
      displayName: "Quarter Final",
      matches: quarter.map((match) => convertBracketMatchToBountyMatch(match, teams)),
    },
    {
      stageId: "SEMI_FINAL",
      displayName: "Semi Final",
      matches: semi.map((match) => convertBracketMatchToBountyMatch(match, teams)),
    },
    {
      stageId: "FINAL",
      displayName: "Final",
      matches: finale.map((match) => convertBracketMatchToBountyMatch(match, teams)),
    },
  ];
}

//  Applies a single stage's results to a partial array of teams
function computeStandingsFromStage(stage: BountyStage, baseTeams: BountyTeam[]): BountyTeam[] {
  const updated = baseTeams.map((t) => ({ ...t }));

  stage.matches.forEach((match) => {
    const { winnerTeamId, loserTeamId, wagerAmount } = match;
    if (!winnerTeamId || !loserTeamId) return;

    const winner = updated.find((t) => t.id === winnerTeamId);
    const loser = updated.find((t) => t.id === loserTeamId);
    if (!winner || !loser) return;

    // Example bounty logic:
    const winnerBounty = !!wagerAmount && wagerAmount > 0 ? winner.bountyValue + wagerAmount : winner.bountyValue;
    const loserBounty = !!wagerAmount && wagerAmount > 0 ? loser.bountyValue + wagerAmount : loser.bountyValue;

    // "Steal" seed if smaller
    winner.bountySeed = Math.min(winner.bountySeed ?? -1, loser.bountySeed ?? -1);
    loser.bountySeed = undefined;

    if (stage.stageId === "FINAL") {
      // The winner takes all scenario
      winner.banked += winnerBounty + loser.bountyValue;
      winner.bountyValue = 0;
    } else {
      // 50% logic
      winner.bountyValue += loserBounty * 0.5;
      winner.banked += loserBounty * 0.5;
    }

    // e.g., subtract wager from loser’s bank
    loser.banked = !!wagerAmount && wagerAmount > 0 ? loser.banked - wagerAmount : loser.banked;
    loser.eliminated = true;
    loser.bountyValue = 0;
  });

  // Sort them so eliminated go last, etc.
  return updated.sort((a, b) => {
    if (a.eliminated && !b.eliminated) return 1;
    if (!a.eliminated && b.eliminated) return -1;

    // Then by bountyValue desc
    if (b.bountyValue !== a.bountyValue) {
      return b.bountyValue - a.bountyValue;
    }
    // Then by banked desc
    if (b.banked !== a.banked) {
      return b.banked - a.banked;
    }
    // Then by seed asc
    return (a.bountySeed ?? -1) - (b.bountySeed ?? -1);
  });
}

function refreshStagesCascade(
  stages: BountyStage[],
  baseTeams: BountyTeam[],
): { newStages: BountyStage[]; finalStandings: BountyTeam[] } {
  let partialStandings = baseTeams.map((t) => ({ ...t }));
  const newStages: BountyStage[] = stages.map((stage) => {
    const refreshed: BountyStage = {
      ...stage,
      matches: stage.matches.map((m) => {
        const updatedA = m.teamA ? partialStandings.find((pt) => pt.id === m.teamA!.id) || null : null;
        const updatedB = m.teamB ? partialStandings.find((pt) => pt.id === m.teamB!.id) || null : null;
        return {
          ...m,
          teamA: updatedA,
          teamB: updatedB,
        };
      }),
    };

    partialStandings = computeStandingsFromStage(refreshed, partialStandings);

    return refreshed;
  });
  return { newStages, finalStandings: partialStandings };
}

function resetStageMatches(stage: BountyStage, keepTeamA: boolean): BountyStage {
  return {
    ...stage,
    matches: stage.matches.map((m) => ({
      ...m,
      wagerAmount: undefined,
      teamA: keepTeamA ? m.teamA : null,
      teamB: null,
      winnerTeamId: undefined,
      loserTeamId: undefined,
    })),
  };
}

/** Gather up winners from partial standings for next stage seeding. */
function getWinnersFromPartial(stage: BountyStage, partial: BountyTeam[]): BountyTeam[] {
  const winners: BountyTeam[] = [];
  for (const match of stage.matches) {
    if (!match.winnerTeamId) continue;
    const w = partial.find((pt) => pt.id === match.winnerTeamId);
    if (w) winners.push(w);
  }
  return winners;
}

/**
 * Sort winners by bountySeed ascending, then place lower half seeds in `teamA`
 * and higher half seeds in `teamB`. If FINAL, place the first 2 in match(0).
 */
function populateStageWithWinners(stage: BountyStage, winners: BountyTeam[]): BountyStage {
  winners.sort((a, b) => (a.bountySeed ?? -1) - (b.bountySeed ?? -1));

  // If final, assume 2 or fewer winners
  if (stage.stageId === "FINAL") {
    const newMatches = stage.matches.map((m) => {
      const teamA = winners[0] || null;
      const teamB = winners[1] || null;
      return {
        ...m,
        teamA,
        teamB,
        winnerTeamId: undefined,
        loserTeamId: undefined,
      };
    });
    return { ...stage, matches: newMatches };
  }

  const half = Math.floor(winners.length / 2);
  const lowerSeeds = winners.slice(half);

  const newMatches = stage.matches.map((m, i) => {
    const teamA = lowerSeeds[i] || null;
    return {
      ...m,
      teamA,
      wagerAmount: undefined,
      teamB: null,
      winnerTeamId: undefined,
      loserTeamId: undefined,
    };
  });

  return { ...stage, matches: newMatches };
}

interface BountyCalculatorProviderProps {
  initialBrackets: Bracket[];
  initialPrediction?: Prediction | null;
  initialTeamSlots: TournamentTeamSlot[];
  children: ReactNode;
}

export const BountyCalculatorProvider = ({
  initialBrackets,
  initialTeamSlots,
  initialPrediction,
  children,
}: BountyCalculatorProviderProps) => {
  const initialTeamsWithBounties = useMemo(() => {
    const teams: BountyTeam[] = [];

    for (const slot of initialTeamSlots) {
      if (!slot.team) continue;

      teams.push({
        id: slot.team.id,
        name: slot.team.name,
        shortName: slot.team.shortName ?? slot.team.name,
        bountyValue: slot.metadata?.bountyValue ?? 0,
        bountySeed: slot.metadata?.seed ?? undefined,
        banked: 0,
        eliminated: false,
      });
    }

    return teams;
  }, [initialTeamSlots]);

  const initialStages = useMemo(() => {
    return mapBracketsToStages(initialBrackets, initialTeamsWithBounties);
  }, [initialBrackets, initialTeamsWithBounties]);

  const [stagesState, setStagesState] = useState<BountyStage[]>(initialStages);
  const [loadingPrediction] = useState(!!initialPrediction);

  useEffect(() => {
    setStagesState(mapBracketsToStages(initialBrackets, initialTeamsWithBounties));
  }, [initialBrackets, initialTeamsWithBounties]);

  const { newStages: renderedStages, finalStandings: currentStandings } = useMemo(() => {
    return refreshStagesCascade(stagesState, initialTeamsWithBounties);
  }, [stagesState, initialTeamsWithBounties]);

  /**
   * 5) Update any match -> reset subsequent -> re-populate winners
   *    using partial-standings approach
   */
  const updateMatch = useCallback(
    (matchId: string, updates: Partial<BountyMatch>) => {
      setStagesState((prev) => {
        const newStages = structuredClone(prev) as BountyStage[];

        // 1) Find stage that has this match
        let changedIdx = -1;
        for (let i = 0; i < newStages.length; i++) {
          const matchIndex = newStages[i].matches.findIndex((m) => m.id === matchId);
          if (matchIndex >= 0) {
            newStages[i].matches[matchIndex] = {
              ...newStages[i].matches[matchIndex],
              ...updates,
            };
            changedIdx = i;
            break;
          }
        }
        if (changedIdx < 0) return prev;

        // 2) Reset subsequent
        for (let i = changedIdx + 1; i < newStages.length; i++) {
          newStages[i] = resetStageMatches(newStages[i], false);
        }

        // 3) Build partial stands from stage0..end
        let partial = initialTeamsWithBounties.map((t) => ({ ...t }));

        // first apply earlier stages
        for (let i = 0; i < changedIdx; i++) {
          partial = computeStandingsFromStage(newStages[i], partial);
        }

        // now from changedIdx onward
        for (let i = changedIdx; i < newStages.length; i++) {
          // apply current stage
          partial = computeStandingsFromStage(newStages[i], partial);

          // **Only** if *every* match in stage i has a winner,
          // auto-populate i+1 from partial
          const allMatchesHaveWinners = newStages[i].matches.every((m) => m.winnerTeamId);
          if (allMatchesHaveWinners && i < newStages.length - 1) {
            const winners = getWinnersFromPartial(newStages[i], partial);
            newStages[i + 1] = populateStageWithWinners(newStages[i + 1], winners);
          }
        }

        // If this stage has exactly one match missing teamB and exactly one possible "winner" from last stage remains, fill it.
        if (changedIdx > 0) {
          const stage = newStages[changedIdx];
          const missingTeamB = stage.matches.filter((m) => !m.teamB);
          if (missingTeamB.length === 1) {
            const prevStage = newStages[changedIdx - 1];
            if (prevStage) {
              const winnerIds = prevStage.matches.map((m) => m.winnerTeamId).filter(Boolean) as string[];
              const possibleTeams = partial.filter((t) => winnerIds.includes(t.id));

              const usedTeamIds = new Set(
                stage.matches.flatMap((m) => [m.teamA?.id, m.teamB?.id]).filter((id): id is string => !!id),
              );

              const leftoverTeam = possibleTeams.find((t) => !usedTeamIds.has(t.id));
              if (leftoverTeam) {
                const slotMatchId = missingTeamB[0].id;
                const matchIndex = stage.matches.findIndex((m) => m.id === slotMatchId);
                if (matchIndex >= 0) {
                  stage.matches[matchIndex] = {
                    ...stage.matches[matchIndex],
                    teamB: leftoverTeam,
                    wagerAmount: undefined,
                    winnerTeamId: undefined,
                    loserTeamId: undefined,
                  };
                }
              }
            }
          }
        }

        return newStages;
      });
    },
    [initialTeamsWithBounties],
  );

  // All the helpers
  const setMatchTeamA = useCallback(
    (matchId: string, teamId: string | null) => {
      const team = teamId ? currentStandings.find((t) => t.id === teamId) || null : null;
      updateMatch(matchId, {
        teamA: team,
        winnerTeamId: undefined,
        loserTeamId: undefined,
      });
    },
    [currentStandings, updateMatch],
  );

  const setMatchTeamB = useCallback(
    (matchId: string, teamId: string | null) => {
      const prevMatch = stagesState.flatMap((s) => s.matches).find((m) => m.id === matchId);
      if (!prevMatch) return;

      const initialTeam = initialTeamsWithBounties.find((t) => t.id === teamId);
      const currentStanding = currentStandings.find((s) => s.id === teamId);
      const updatedTeam = currentStanding
        ? {
            ...currentStanding,
            bountyValue: currentStanding.bountyValue ?? initialTeam?.bountyValue ?? 0,
            bountySeed:
              (currentStanding.bountySeed ?? -1) > 0 ? currentStanding.bountySeed : (initialTeam?.bountySeed ?? 0),
            banked: currentStanding.banked ?? 0,
          }
        : null;

      updateMatch(matchId, {
        teamB: updatedTeam,
        wagerAmount: undefined,
        winnerTeamId: undefined,
        loserTeamId: undefined,
      });

      const stageIndex = stagesState.findIndex((stage) => stage.matches.some((m) => m.id === matchId));
      if (stageIndex < 0) return;

      const stage = stagesState[stageIndex];
      const otherMatchesInStage = stage.matches.filter((m) => m.id !== matchId);
      const conflict = otherMatchesInStage.find((m) => m.teamB?.id === teamId);

      if (conflict) {
        updateMatch(conflict.id, {
          teamB: prevMatch.teamB ?? null,
          wagerAmount: undefined,
          winnerTeamId: undefined,
          loserTeamId: undefined,
        });
      }
    },
    [initialTeamsWithBounties, stagesState, updateMatch, currentStandings],
  );

  const setMatchWinner = useCallback(
    (matchId: string, winnerTeamId?: string, loserTeamId?: string) => {
      updateMatch(matchId, { winnerTeamId, loserTeamId });
    },
    [updateMatch],
  );

  const setWagerAmount = useCallback(
    (matchId: string, amount?: number) => {
      updateMatch(matchId, { wagerAmount: amount });
    },
    [updateMatch],
  );

  // Reset / Autofill / Randomize

  const resetStage = useCallback((stageId: BracketStageId) => {
    setStagesState((prev) => {
      const newStages = structuredClone(prev) as BountyStage[];
      const idx = newStages.findIndex((s) => s.stageId === stageId);

      if (idx === -1) return prev;

      // For the current stage: keepTeamA = true
      newStages[idx] = resetStageMatches(newStages[idx], true);

      // For stages after, dispose teamA's
      for (let i = idx + 1; i < newStages.length; i++) {
        newStages[i] = resetStageMatches(newStages[i], false);
      }
      return newStages;
    });
  }, []);

  const autofillStage = useCallback(
    (stageId: BracketStageId) => {
      setStagesState((prev) => {
        const newStages = structuredClone(prev) as BountyStage[];
        const idx = newStages.findIndex((s) => s.stageId === stageId);
        if (idx === -1) return prev;

        newStages[idx] = resetStageMatches(newStages[idx], true);

        // Reset following
        for (let i = idx + 1; i < newStages.length; i++) {
          newStages[i] = resetStageMatches(newStages[i], false);
        }

        //  Compute the standings up to idx
        let partial = initialTeamsWithBounties.map((t) => ({ ...t }));
        for (let i = 0; i < idx; i++) {
          partial = computeStandingsFromStage(newStages[i], partial);
        }

        // For the stage we are autofilling, we fill `teamB` randomly
        const teamAIds = newStages[idx].matches.map((m) => m.teamA?.id).filter((id) => id) as string[];
        const available = partial.filter((t) => !t.eliminated && !teamAIds.includes(t.id));
        const shuffled = [...available].sort(() => Math.random() - 0.5);

        newStages[idx].matches.forEach((m, iMatch) => {
          if (iMatch < shuffled.length) {
            m.teamB = shuffled[iMatch];
          }
          m.winnerTeamId = undefined;
          m.loserTeamId = undefined;
        });

        for (let i = idx; i < newStages.length; i++) {
          partial = computeStandingsFromStage(newStages[i], partial);
          if (i < newStages.length - 1) {
            const winners = getWinnersFromPartial(newStages[i], partial);
            newStages[i + 1] = populateStageWithWinners(newStages[i + 1], winners);
          }
        }

        return newStages;
      });
    },
    [initialTeamsWithBounties],
  );

  const randomiseWinners = useCallback(
    (stageId: BracketStageId) => {
      setStagesState((prev) => {
        const newStages = structuredClone(prev) as BountyStage[];
        const idx = newStages.findIndex((s) => s.stageId === stageId);
        if (idx === -1) return prev;

        // random pick
        const stage = newStages[idx];
        stage.matches = stage.matches.map((m) => {
          if (!m.teamA || !m.teamB) return m;
          const flip = Math.random() > 0.5;
          const winnerTeamId = flip ? m.teamA.id : m.teamB.id;
          const loserTeamId = flip ? m.teamB.id : m.teamA.id;
          return { ...m, winnerTeamId, loserTeamId };
        });

        // reset subsequent
        for (let i = idx + 1; i < newStages.length; i++) {
          newStages[i] = resetStageMatches(newStages[i], false);
        }

        // Build partial stands from stage0..end, as like autofill
        let partial = initialTeamsWithBounties.map((t) => ({ ...t }));

        // first apply earlier stages
        for (let i = 0; i < idx; i++) {
          partial = computeStandingsFromStage(newStages[i], partial);
        }

        // partial stands from 0..end
        for (let i = idx; i < newStages.length; i++) {
          // apply current stage
          partial = computeStandingsFromStage(newStages[i], partial);

          // **Only** if *every* match in stage i has a winner,
          // auto-populate i+1 from partial
          const allMatchesHaveWinners = newStages[i].matches.every((m) => m.winnerTeamId);
          if (allMatchesHaveWinners && i < newStages.length - 1) {
            const winners = getWinnersFromPartial(newStages[i], partial);
            newStages[i + 1] = populateStageWithWinners(newStages[i + 1], winners);
          }
        }

        return newStages;
      });
    },
    [initialTeamsWithBounties],
  );

  const sharePrediction = useCallback(async () => {
    const prediction = mapStagesToShareablePrediction(stagesState);
    const response = await postBountySimulatorPrediction("bounty-2025-season-1", prediction);

    if (!response) return null;
    return response.predictionId;
  }, [stagesState]);

  return (
    <BountyCalculatorContext.Provider
      value={{
        stages: renderedStages,
        currentStandings,
        initialTeams: initialTeamsWithBounties,

        setMatchTeamA,
        setMatchTeamB,
        setMatchWinner,
        setWagerAmount,

        autofillStage,
        resetStage,
        randomiseWinners,

        sharePrediction,
        loadingPrediction,
      }}
    >
      {children}
    </BountyCalculatorContext.Provider>
  );
};

export function useBountyCalculator(): BountyCalculatorProviderValue {
  const ctx = useContext(BountyCalculatorContext);
  if (!ctx) {
    throw new Error("useBountyCalculator must be used within a BountyCalculatorProvider");
  }
  return ctx;
}
